/** Saree length in the shop's words: gaj and girah (1 gaj = 16 girah), plus inches and metres. */
export const GIRAH_PER_GAJ = 16;
export const FULL_SAREE_GIRAH = 80; // 5 gaj
export const NEARLY_FULL = 0.95;

export const gajGirah = (inches: number, inchesPerGirah = 2.6875): string => {
  const girah = Math.max(0, inches) / inchesPerGirah;
  const gaj = Math.floor(girah / GIRAH_PER_GAJ);
  const rest = Math.round((girah - gaj * GIRAH_PER_GAJ) * 2) / 2; // to half a girah
  if (rest >= GIRAH_PER_GAJ) return `${gaj + 1} gaj`;
  if (!gaj) return `${rest} girah`;
  return rest ? `${gaj} gaj ${rest} girah` : `${gaj} gaj`;
};

/** Is the entered length about the full length (the saree's measured length, else 5 gaj)? */
export const lengthCheck = (progress: any, inchesPerGirah = 2.6875) => {
  const entered = Number(progress?.enteredInches || 0);
  const full = Number(progress?.fullInches || progress?.targetInches || FULL_SAREE_GIRAH * inchesPerGirah);
  return { entered, full, ok: entered >= full * NEARLY_FULL };
};

/** More than this in one day is far past a normal day at the loom (a few inches to about 20). */
export const BIG_DAY_INCHES = 40;
/** Sarees run a little long; past this share of the full length an entry is worth a second look. */
export const PAST_FULL = 1.05;

/**
 * Today's entry, in inches, if it looks like a slip (an extra 0, girah typed as inches): it would
 * take the saree well past its full length, or it is far more than one day's weaving. Null if fine.
 */
export const unusualDay = (value: number, unit: "INCH" | "GIRAH", progress: any, inchesPerGirah = 2.6875) => {
  const today = unit === "GIRAH" ? value * inchesPerGirah : value;
  if (!(today > 0)) return null;
  const { entered, full } = lengthCheck(progress, inchesPerGirah);
  if (entered + today > full * PAST_FULL) return { kind: "PAST_FULL" as const, today, after: entered + today, full };
  if (today > BIG_DAY_INCHES) return { kind: "BIG_DAY" as const, today, after: entered + today, full };
  return null;
};

/**
 * Can the saree be marked complete? Only once its length is reached: the inches entered are about
 * full length, or the last check-in measured it about full on the loom (an entry may be missing).
 * `left` is what is still to go by the entered inches.
 */
export const canComplete = (progress: any, inchesPerGirah = Number(progress?.inchesPerGirah) || 2.6875) => {
  const { entered, full, ok } = lengthCheck(progress, inchesPerGirah);
  const measured = Number(progress?.lastCheckIn?.measuredInches || 0);
  const measuredFull = measured > 0 && measured >= full * NEARLY_FULL;
  return { ok: ok || measuredFull, entered, full, left: Math.max(0, full - entered) };
};
